/**
 * Scroll Animations & Page Transitions
 *
 * Coordinates with the inline head script that sets
 * [data-scroll-anim-pending] on <html> to pre-hide animated content. This
 * script marks each section, then removes the html flag and progressively
 * reveals sections (above-the-fold immediately, others via IntersectionObserver).
 */

(function () {
  const settingsEl = document.getElementById('scroll-animation-settings');
  if (!settingsEl) return;

  let settings;
  try {
    settings = JSON.parse(settingsEl.textContent);
  } catch (e) {
    document.documentElement.removeAttribute('data-scroll-anim-pending');
    return;
  }

  const { animationType, stagger, pageFade } = settings;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesignMode = document.documentElement.classList.contains('shopify-design-mode');

  // ==========================================================================
  // Page Fade Transition
  // ==========================================================================

  if (pageFade && !prefersReducedMotion && !isDesignMode) {
    document.body.setAttribute('data-page-fade', '');

    document.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (!link) return;

      const href = link.getAttribute('href');

      if (
        !href ||
        href.startsWith('#') ||
        href.startsWith('javascript:') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        link.hasAttribute('target') ||
        link.hasAttribute('download') ||
        e.ctrlKey ||
        e.metaKey ||
        e.shiftKey
      ) {
        return;
      }

      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;
      } catch {
        return;
      }

      e.preventDefault();
      document.body.classList.add('is-leaving');
      setTimeout(() => {
        window.location.href = href;
      }, 300);
    });
  }

  // ==========================================================================
  // Scroll Animations
  // ==========================================================================

  function clearPendingFlag() {
    document.documentElement.removeAttribute('data-scroll-anim-pending');
  }

  if (!animationType || prefersReducedMotion || isDesignMode) {
    clearPendingFlag();
    return;
  }

  const mainContent = document.getElementById('MainContent');
  if (!mainContent) {
    clearPendingFlag();
    return;
  }

  // Selectors whose direct children should stagger.
  // Each entry: container selector + child selector (relative to container).
  const STAGGER_GROUPS = [
    { container: '.product-grid', child: '.product-grid__item' },
    { container: '.resource-list', child: '.resource-list__item' },
    { container: '.group-block-content', child: '.group-block, .c-group-block' },
    { container: '.section-content-wrapper', child: '.group-block, .c-group-block, .image-block, .c-image-block' },
    { container: '[class*="1-image-hover-grid"]', child: '[class*="1-image-hover-group"]' },
    { container: '.c-menu-showcase__list', child: '.c-menu-showcase__item' },
    { container: '.c-locations-list__main', child: '.c-locations-list__state-group' },
    { container: '.c-locations-list__accordion', child: 'accordion-custom' },
  ];

  // Combined selector for finding any stagger item (used for nesting lookup).
  const ANY_STAGGER_ITEM = STAGGER_GROUPS
    .map(({ container, child }) => `${container} > :is(${child})`)
    .join(', ');

  function applyStaggerDelays(section) {
    // First pass: assign each item a per-parent index (so siblings stagger
    // relative to each other, not globally across the section).
    const baseDelays = new Map();
    STAGGER_GROUPS.forEach(({ container, child }) => {
      section.querySelectorAll(container).forEach((parent) => {
        let index = 0;
        for (const el of parent.children) {
          if (el.matches(child)) {
            baseDelays.set(el, index * stagger);
            index += 1;
          }
        }
      });
    });

    // Second pass: nested items wait for their nearest stagger ancestor to
    // start animating before they begin (offset by one stagger unit) — so
    // a parent group visibly appears first, then its children cascade in.
    baseDelays.forEach((baseDelay, el) => {
      const ancestor = el.parentElement && el.parentElement.closest(ANY_STAGGER_ITEM);
      const ancestorDelay = ancestor && baseDelays.has(ancestor) ? baseDelays.get(ancestor) : 0;
      const ancestorOffset = ancestor ? stagger : 0;
      el.style.animationDelay = `${baseDelay + ancestorDelay + ancestorOffset}ms`;
    });
  }

  // Match the observer's intersection criteria so a section the observer
  // would NOT yet trigger for (e.g. barely poking in from the bottom)
  // doesn't get its stagger played on initial load.
  const OBSERVER_BOTTOM_MARGIN = 50;
  const OBSERVER_THRESHOLD = 0.1;

  function isInitiallyVisible(section) {
    const rect = section.getBoundingClientRect();
    if (rect.height === 0) return false;
    const visibleTop = Math.max(rect.top, 0);
    const visibleBottom = Math.min(rect.bottom, window.innerHeight - OBSERVER_BOTTOM_MARGIN);
    const visible = Math.max(0, visibleBottom - visibleTop);
    return visible / rect.height >= OBSERVER_THRESHOLD;
  }

  const sections = mainContent.querySelectorAll('.shopify-section');
  const aboveFoldSections = [];

  sections.forEach((section) => {
    // Skip Klaviyo sections - their forms need to render at full opacity.
    if (section.querySelector('[class*="klaviyo-form-"]')) return;

    section.setAttribute('data-scroll-animation', animationType);
    section.classList.add('scroll-animate-hidden');
    applyStaggerDelays(section);

    if (isInitiallyVisible(section)) {
      aboveFoldSections.push(section);
    }
  });

  // Two animation frames so the browser commits the pre-hidden state before
  // we transition to visible — guarantees the transition fires.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      clearPendingFlag();
      aboveFoldSections.forEach((section) => {
        section.classList.remove('scroll-animate-hidden');
        section.classList.add('is-visible');
      });
    });
  });

  // IntersectionObserver for below-the-fold sections.
  // Sections entering together get a stagger between them.
  let pendingAnimations = [];
  let animationFrame = null;

  function processAnimations() {
    pendingAnimations.forEach((el, index) => {
      setTimeout(() => {
        el.classList.remove('scroll-animate-hidden');
        el.classList.add('is-visible');
      }, index * stagger);
    });
    pendingAnimations = [];
    animationFrame = null;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          pendingAnimations.push(entry.target);
          observer.unobserve(entry.target);
        }
      });

      if (pendingAnimations.length > 0 && !animationFrame) {
        animationFrame = requestAnimationFrame(processAnimations);
      }
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    }
  );

  document
    .querySelectorAll('[data-scroll-animation].scroll-animate-hidden')
    .forEach((el) => observer.observe(el));

  // Re-handle dynamically loaded sections (e.g., theme editor changes).
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.closest('.shopify-section') || event.target;
    if (!section || !section.classList || !section.classList.contains('shopify-section')) return;
    if (section.querySelector('[class*="klaviyo-form-"]')) return;
    if (section.hasAttribute('data-scroll-animation')) return;

    section.setAttribute('data-scroll-animation', animationType);
    applyStaggerDelays(section);

    if (isInitiallyVisible(section)) {
      requestAnimationFrame(() => section.classList.add('is-visible'));
    } else {
      section.classList.add('scroll-animate-hidden');
      observer.observe(section);
    }
  });
})();
